import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  fetchShopFile,
  isShopAccessRequired,
  openShopAccess,
  opsErrorMessage,
  shopGrant,
  type ShopFile,
} from '../lib/ops-api';
import {
  REASON_MIN_LENGTH,
  SHOP_FILE_BASE,
  SHOP_FILE_TABS,
  isReasonAcceptable,
  shopFilePath,
} from '../lib/shop-file';
import { useLocale } from '../i18n/LocaleProvider';
import { PageBody, PageHead } from './Page';
import { Pill } from './Pill';
import { StatusBanner } from './StatusBanner';
import { Icon } from './Brand';

/** What every tab of the shop file receives. */
export interface ShopFileContext {
  readonly shopId: string;
  readonly file: ShopFile;
  /** The ticket the agent came from, when they came from one. */
  readonly ticketId: string | null;
  /** Re-reads the file — after an action changed something the overview shows. */
  readonly reload: () => void;
  /**
   * Call when a tab's own request came back {@link isShopAccessRequired}: drops the grant and puts
   * the reason prompt back in front of the file.
   */
  readonly requireAccess: () => void;
}

/** The shop file's context, for a tab rendered inside {@link ShopFileLayout}. */
export function useShopFile(): ShopFileContext {
  return useOutletContext<ShopFileContext>();
}

/**
 * One shop's file: the reason prompt in front of it, a header, a tab strip and the open tab.
 *
 * **Nothing about the shop is read before a reason is given.** The API enforces that (every read
 * needs the grant the reason buys, see `lib/shop-file.ts`), so the prompt here is not the control
 * — it is the only way through it. An agent who arrived from a ticket (`?ticket=`) opens the file
 * with that ticket as the reason in one click; anyone else types why.
 */
export function ShopFileLayout() {
  const { t, tCount } = useLocale();
  const { shopId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const ticketId = searchParams.get('ticket');
  const queryClient = useQueryClient();
  // Seeded from the store, so a reload within the grant's lifetime does not ask again.
  const [hasGrant, setHasGrant] = useState(() => shopGrant(shopId) !== '');

  const file = useQuery({
    queryKey: ['shop-file', shopId],
    queryFn: () => fetchShopFile(shopId),
    enabled: hasGrant && shopId !== '',
    retry: false,
  });

  const accessRefused =
    file.isError && isShopAccessRequired(file.error, shopId);

  function requireAccess(): void {
    setHasGrant(false);
    queryClient.removeQueries({ queryKey: ['shop-file', shopId] });
  }

  if (!hasGrant || accessRefused) {
    return (
      <AccessGate
        shopId={shopId}
        ticketId={ticketId}
        onGranted={() => {
          setHasGrant(true);
          void queryClient.invalidateQueries({
            queryKey: ['shop-file', shopId],
          });
        }}
      />
    );
  }

  if (file.isLoading || !file.data) {
    return (
      <PageBody>
        {file.isError ? (
          <StatusBanner tone="error">
            {opsErrorMessage(file.error, t('shopFile.loadFailed'))}
          </StatusBanner>
        ) : (
          <StatusBanner tone="loading">{t('common.loading')}</StatusBanner>
        )}
      </PageBody>
    );
  }

  const data = file.data;
  const context: ShopFileContext = {
    shopId,
    file: data,
    ticketId,
    reload: () => void file.refetch(),
    requireAccess,
  };

  return (
    <>
      <PageHead
        eyebrow={t('shopFile.eyebrow')}
        title={data.identity.name || data.identity.shopDomain}
        lead={data.identity.shopDomain}
        actions={
          <Link to={SHOP_FILE_BASE} className="btn-secondary btn-sm gap-2">
            <Icon name="arrowLeft" className="h-4 w-4" />
            {t('shopFile.backToSearch')}
          </Link>
        }
      >
        <div className="flex flex-wrap gap-2">
          {data.subscription ? (
            <Pill
              tone={data.subscription.status === 'ACTIVE' ? 'teal' : 'amber'}
            >
              {data.subscription.tier} · {data.subscription.status}
              {data.subscription.isInTrial ? ` · ${t('shopFile.trial')}` : ''}
            </Pill>
          ) : (
            <Pill tone="gray">{t('shopFile.noSubscription')}</Pill>
          )}
          {data.identity.pendingDeletion && (
            <Pill tone="red">{t('shopFile.pendingDeletion')}</Pill>
          )}
          {!data.setup.numberingConfigured && (
            <Pill tone="red">{t('shopFile.onboardingIncomplete')}</Pill>
          )}
          {(data.openAttentionItems?.length ?? 0) > 0 && (
            <Pill tone="amber">
              {tCount(
                'shopFile.openAttentionItems',
                data.openAttentionItems?.length ?? 0,
              )}
            </Pill>
          )}
        </div>
      </PageHead>

      <ShopFileTabs shopId={shopId} ticketId={ticketId} />

      <Outlet context={context} />
    </>
  );
}

/**
 * The shop file's tab strip. Nine tabs do not fit a phone, so the strip scrolls sideways — and the
 * active tab is scrolled into view whenever it changes, or a deep link to Nummernkreis would open
 * with its own tab out of sight to the right and nothing saying where the operator is.
 */
function ShopFileTabs({
  shopId,
  ticketId,
}: {
  readonly shopId: string;
  readonly ticketId: string | null;
}) {
  const { t } = useLocale();
  const { pathname } = useLocation();
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    navRef.current
      ?.querySelector('[aria-current="page"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [pathname]);

  return (
    <div className="bg-white border-b border-gray-200">
      <nav
        ref={navRef}
        className="scroll-strip px-4 sm:px-6 lg:px-8 flex gap-1 overflow-x-auto"
        aria-label={t('shopFile.tabsLabel')}
      >
        {SHOP_FILE_TABS.map(({ tab, labelKey }) => (
          <NavLink
            key={tab || 'overview'}
            to={shopFilePath(shopId, tab, ticketId ?? undefined)}
            end={tab === ''}
            className={({ isActive }) =>
              clsx(
                'inline-flex items-center px-3 min-h-[44px] border-b-2 text-sm font-medium whitespace-nowrap transition-colors',
                isActive
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-600 hover:text-primary-700 hover:border-gray-300',
              )
            }
          >
            {t(labelKey)}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/**
 * The reason prompt. A ticket, when the agent came from one, is offered as the reason; otherwise
 * a typed reason of at least {@link REASON_MIN_LENGTH} characters — the API's own floor.
 */
function AccessGate({
  shopId,
  ticketId,
  onGranted,
}: {
  readonly shopId: string;
  readonly ticketId: string | null;
  readonly onGranted: () => void;
}) {
  const { t } = useLocale();
  const [reason, setReason] = useState('');

  const open = useMutation({
    mutationFn: (input: { reason?: string; ticketId?: string }) =>
      openShopAccess(shopId, input),
    onSuccess: onGranted,
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!isReasonAcceptable(reason)) return;
    open.mutate({
      reason: reason.trim(),
      ...(ticketId ? { ticketId } : {}),
    });
  }

  return (
    <>
      <PageHead
        eyebrow={t('shopFile.eyebrow')}
        title={t('shopFile.gate.title')}
        lead={t('shopFile.gate.lead')}
        actions={
          <Link to={SHOP_FILE_BASE} className="btn-secondary btn-sm gap-2">
            <Icon name="arrowLeft" className="h-4 w-4" />
            {t('shopFile.backToSearch')}
          </Link>
        }
      />
      <PageBody narrow>
        {ticketId && (
          <div className="card space-y-3">
            <p className="text-sm text-gray-700">
              {t('shopFile.gate.fromTicket')}
            </p>
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={open.isPending}
              onClick={() => open.mutate({ ticketId })}
            >
              {t('shopFile.gate.openWithTicket')}
            </button>
          </div>
        )}

        <form onSubmit={submit} className="card space-y-3">
          <label htmlFor="shopAccessReason" className="field-label">
            {t('shopFile.gate.reasonLabel')}
          </label>
          <textarea
            id="shopAccessReason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            maxLength={500}
            placeholder={t('shopFile.gate.reasonPlaceholder')}
            className="field-input"
          />
          <p className="field-hint">
            {t('shopFile.gate.reasonHint', { min: REASON_MIN_LENGTH })}
          </p>
          <button
            type="submit"
            className="btn-primary btn-sm"
            disabled={!isReasonAcceptable(reason) || open.isPending}
          >
            {t('shopFile.gate.open')}
          </button>
        </form>

        {open.isError && (
          <StatusBanner tone="error">
            {opsErrorMessage(open.error, t('shopFile.gate.failed'))}
          </StatusBanner>
        )}
      </PageBody>
    </>
  );
}
